import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertTriangle, Trash2 } from 'lucide-react'
import type { Location } from '@/types/entities'

interface LocationDeleteDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    location: Location | null
    onConfirm: () => void
    isLoading?: boolean
}

export function LocationDeleteDialog({
    open,
    onOpenChange,
    location,
    onConfirm,
    isLoading = false,
}: LocationDeleteDialogProps) {
    const { t } = useTranslation()
    if (!location) return null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Trash2 className="h-5 w-5 text-destructive" />
                        {t('locations.deleteLocationTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('locations.confirmDelete')} "{location.description}"?
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {location.noofObjects && location.noofObjects > 0 ? (
                        <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                                {t('locations.cannotDeleteInUse', { count: location.noofObjects })}
                            </AlertDescription>
                        </Alert>
                    ) : (
                        <Alert>
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                                {t('locations.deleteWarning')}
                            </AlertDescription>
                        </Alert>
                    )}

                    <div className="bg-muted p-3 rounded-lg">
                        <div className="text-sm space-y-1">
                            <div>
                                <strong>{t('locations.description')}:</strong> {location.description || '-'}
                            </div>
                            {location.city && (
                                <div>
                                    <strong>{t('locations.city')}:</strong> {location.city}
                                </div>
                            )}
                            {location.country && (
                                <div>
                                    <strong>{t('locations.country')}:</strong> {location.country}
                                </div>
                            )}
                            {location.category && (
                                <div>
                                    <strong>{t('locations.category')}:</strong> {location.category}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={onConfirm}
                        disabled={isLoading || (location.noofObjects !== undefined && location.noofObjects > 0)}
                    >
                        {isLoading ? t('locations.deleting') : t('locations.deleteLocation')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

